"use client"

import { useLayoutEffect, useRef } from "react"

interface SmartStickyProps {
  offsetTop?: number
  offsetBottom?: number
  className?: string
  children: React.ReactNode
}

export function SmartSticky({
  offsetTop = 80,
  offsetBottom = 16,
  className,
  children,
}: SmartStickyProps) {
  const innerRef = useRef<HTMLDivElement>(null)
  const lastScrollY = useRef(0)
  const currentTop = useRef(offsetTop)
  const ticking = useRef(false)

  useLayoutEffect(() => {
    const inner = innerRef.current
    if (!inner) return

    const update = () => {
      ticking.current = false
      const vh = window.innerHeight
      const h = inner.offsetHeight
      const scrollY = window.scrollY
      const delta = scrollY - lastScrollY.current
      lastScrollY.current = scrollY

      if (h + offsetTop + offsetBottom <= vh) {
        currentTop.current = offsetTop
      } else {
        const minTop = vh - h - offsetBottom
        const maxTop = offsetTop
        const next = currentTop.current - delta
        currentTop.current = Math.max(minTop, Math.min(maxTop, next))
      }
      inner.style.top = `${currentTop.current}px`
    }

    const onScroll = () => {
      if (!ticking.current) {
        requestAnimationFrame(update)
        ticking.current = true
      }
    }

    lastScrollY.current = window.scrollY
    currentTop.current = offsetTop
    update()

    window.addEventListener("scroll", onScroll, { passive: true })
    window.addEventListener("resize", update)

    const ro = new ResizeObserver(update)
    ro.observe(inner)

    return () => {
      window.removeEventListener("scroll", onScroll)
      window.removeEventListener("resize", update)
      ro.disconnect()
    }
  }, [offsetTop, offsetBottom])

  return (
    <div className={className}>
      <div
        ref={innerRef}
        style={{
          position: "sticky",
          top: offsetTop,
          willChange: "top",
        }}
      >
        {children}
      </div>
    </div>
  )
}
